'use client';

import { useQuery, useMutation } from 'convex/react';
import { api } from '@/convex/_generated/api';
import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Code,
  ArrowUpDown,
  Users,
  CheckCircle2,
  TrendingUp,
  ArrowDown,
  ArrowUp,
  Clock,
  Check,
  ShieldCheck,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useSearchParams, useRouter } from 'next/navigation';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import ScraperFilterSidebar from '@/components/filters/ScraperFilterSidebar';
import ScraperMobileFilterDrawer from '@/components/filters/ScraperMobileFilterDrawer';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { useUser } from '@clerk/nextjs';
import { useScraperStore } from '@/stores/useScraperStore';
import { useScraperUrlSync } from '@/hooks/useScraperUrlSync';
import ScraperCard from '@/components/features/scraper/ScraperCard';
import ScraperSearchBar from '@/components/features/scraper/ScraperSearchBar';
import { Label } from '@/components/ui/label';
import type { Doc } from '@/convex/_generated/dataModel';

const ITEMS_PER_PAGE = 12;

const sortOptions = [
  { value: 'recommended', label: 'Recommended', Icon: CheckCircle2 },
  { value: 'top_rated', label: 'Top Rated', Icon: TrendingUp },
  { value: 'newest', label: 'Newest', Icon: ArrowDown },
  { value: 'price_asc', label: 'Price: Low-High', Icon: ArrowUp },
  { value: 'price_desc', label: 'Price: High-Low', Icon: ArrowDown },
  { value: 'delivery_asc', label: 'Fastest Delivery', Icon: Clock },
];

export default function ScrapersPageClient() {
  // Get values and actions from Zustand store
  const {
    currentPage,
    sortBy,
    searchTerm,
    sourcePlatforms,
    serviceType,
    priceRange,
    deliveryTime,
    providerRating,
    setCurrentPage,
    setSortBy,
  } = useScraperStore();

  // Sync URL with store state
  useScraperUrlSync();

  const router = useRouter();
  const { user } = useUser();

  // Local UI state
  const [modalOpen, setModalOpen] = useState(false);
  const [requestDescription, setRequestDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // State for results display
  const [displayedScrapers, setDisplayedScrapers] = useState<Doc<'scrapers'>[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Custom scraper request mutation
  const createCustomScraperRequest = useMutation(api.customScraperRequests.create);

  // Prepare query arguments
  const queryArgs = {
    searchTerm: searchTerm || undefined,
    sourcePlatforms: sourcePlatforms?.length ? sourcePlatforms : undefined,
    serviceType: serviceType || undefined,
    page: currentPage,
    pageSize: ITEMS_PER_PAGE,
    sortBy,
    priceMin: priceRange.min,
    priceMax: priceRange.max,
    deliveryTime,
    providerRating,
  };

  // Query for scrapers with advanced filters from store
  const result = useQuery(api.scrapers.getWithAdvancedFilters, queryArgs);

  // Handle results and update state
  useEffect(() => {
    if (result) {
      setDisplayedScrapers(result.scrapers || []);
      setTotalCount(result.total || 0);
      setTotalPages(result.totalPages || Math.ceil((result.total || 0) / ITEMS_PER_PAGE));
    }
  }, [result]);

  // Effect to reset pagination when filters change (excluding sortBy and currentPage)
  useEffect(() => {
    setCurrentPage(1); // Reset store page to 1
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, sourcePlatforms, serviceType, priceRange, deliveryTime, providerRating]);

  // Determine if any filters are active (excluding sortBy and page)
  const hasActiveFilters = useMemo(() => {
    return (
      !!searchTerm ||
      (sourcePlatforms && sourcePlatforms.length > 0) ||
      !!serviceType ||
      priceRange.min !== null ||
      priceRange.max !== null ||
      deliveryTime !== null ||
      providerRating !== null
    );
  }, [searchTerm, sourcePlatforms, serviceType, priceRange, deliveryTime, providerRating]);

  // Handle page change with rate limiting
  const handlePageChange = useCallback(
    (newPage: number) => {
      const lastPageChange = sessionStorage.getItem('lastScraperPageChange');
      const now = Date.now();
      if (lastPageChange && now - parseInt(lastPageChange, 10) < 1000) {
        return;
      }

      sessionStorage.setItem('lastScraperPageChange', now.toString());
      setCurrentPage(newPage);
    },
    [setCurrentPage],
  );

  // Handle sort change
  const handleSortChange = useCallback(
    (value: string) => {
      setSortBy(value);
    },
    [setSortBy],
  );

  // Handler for submitting the custom scraper request
  const handleCustomScraperRequest = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user) {
      setSubmitError('You must be logged in to request a custom scraping service');
      return;
    }

    if (requestDescription.length < 10) {
      setSubmitError('Please provide a more detailed description');
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      await createCustomScraperRequest({ description: requestDescription });
      setSubmitSuccess(true);
      setRequestDescription('');
    } catch (error) {
      setSubmitError('Failed to submit request. Please try again later.');
      console.error('Error submitting custom scraper request:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const currentSortOption = sortOptions.find((opt) => opt.value === sortBy);

  return (
    <>
      {/* Custom Scraper Request Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Request a Custom Scraping Service</DialogTitle>
            <DialogDescription>
              Describe your data needs and requirements. Our team will connect you with the right
              provider.
            </DialogDescription>
          </DialogHeader>
          {submitSuccess ? (
            <div className="py-6 text-center">
              <div className="bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 p-4 rounded-lg mb-4">
                <Check className="w-6 h-6 mx-auto mb-2" />
                <p className="font-medium">Request submitted successfully!</p>
              </div>
              <p className="text-muted-foreground mb-4">
                We've received your request and will get back to you shortly.
              </p>
              <Button onClick={() => setModalOpen(false)}>Close</Button>
            </div>
          ) : (
            <form onSubmit={handleCustomScraperRequest} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="description">Describe your scraping needs</Label>
                <Textarea
                  id="description"
                  value={requestDescription}
                  onChange={(e) => setRequestDescription(e.target.value)}
                  placeholder="Please describe the data you need, source platforms, volume requirements, and any specific fields or criteria..."
                  className="min-h-[120px]"
                />
                {submitError && <div className="text-red-600 text-sm">{submitError}</div>}
                <DialogFooter>
                  <Button
                    type="submit"
                    disabled={isSubmitting || requestDescription.length < 10}
                    className="w-full"
                  >
                    {isSubmitting ? <LoadingSpinner /> : 'Submit Request'}
                  </Button>
                </DialogFooter>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
      <div className="container px-4 py-6 sm:py-8 lg:py-10 max-w-screen-xl mx-auto">
        <div className="flex flex-col md:flex-row gap-6 md:gap-8">
          {/* Desktop Filter Sidebar - Hidden on Mobile */}
          <div className="hidden md:block w-full md:w-64 lg:w-72 shrink-0">
            <ScraperFilterSidebar className="sticky top-24" />
          </div>

          {/* Main Content Area */}
          <div className="flex-1 min-w-0">
            {/* Search and Category Area */}
            <div className="flex flex-col sm:flex-row gap-4 mb-6">
              <div className="flex-1">
                <ScraperSearchBar placeholder="Search scrapers by skill, platform, provider..." />
              </div>

              {/* New container for Sort and Filter buttons */}
              <div className="flex flex-row gap-2 w-full sm:w-auto items-center">
                <div className="flex-1">
                  {' '}
                  {/* Sort Dropdown takes available space */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full flex items-center justify-between gap-1.5"
                      >
                        <span>
                          {sortOptions.find((option) => option.value === sortBy)?.label ||
                            'Sort By'}
                        </span>
                        <ArrowUpDown className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-[200px]">
                      {sortOptions.map((option) => (
                        <DropdownMenuItem
                          key={option.value}
                          onClick={() => handleSortChange(option.value)}
                          className="flex items-center gap-2"
                        >
                          <option.Icon className="h-4 w-4" />
                          <span>{option.label}</span>
                          {sortBy === option.value && <Check className="h-4 w-4 ml-auto" />}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <div className="flex-shrink-0">
                  {' '}
                  {/* Container for MobileFilterDrawer */}
                  <ScraperMobileFilterDrawer />
                </div>
              </div>
            </div>

            {/* Results Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4 sm:mb-6">
              <div>
                <h1 className="text-2xl font-semibold tracking-tight">
                  <span className="text-muted-foreground">Data Scraping Services</span>
                </h1>
                {hasActiveFilters && (
                  <div className="text-sm text-muted-foreground mt-1">Filtered results</div>
                )}
              </div>
            </div>

            {/* Loading State */}
            {!result ? (
              <div className="flex justify-center py-16">
                <LoadingSpinner />
              </div>
            ) : displayedScrapers && displayedScrapers.length > 0 ? (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                  {displayedScrapers.map((scraper) => (
                    <ScraperCard key={scraper._id} scraperId={scraper._id} />
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center py-6 sm:py-10 lg:py-16 px-4 sm:px-8 bg-card rounded-xl border border-border">
                <Code className="w-10 h-10 sm:w-12 sm:h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-base sm:text-lg lg:text-xl font-medium text-foreground mb-2">
                  No matching scraping services found
                </h3>
                <p className="text-sm sm:text-base text-muted-foreground mb-4">
                  Try broadening your search criteria or using different filters
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-2 max-w-xs sm:max-w-none mx-auto">
                  <Button
                    variant="outline"
                    onClick={() => setModalOpen(true)}
                    className="flex items-center gap-2 w-full sm:w-auto"
                  >
                    Request a Custom Scraping Service
                  </Button>
                </div>
              </div>
            )}

            {/* Pagination & Total Count */}
            {totalPages > 0 && displayedScrapers.length > 0 && (
              <div className="flex flex-col items-center justify-center gap-3 mt-8 mb-4">
                {/* Total count display */}
                {totalCount > 0 && (
                  <div className="text-center mb-2">
                    <span className="text-base font-medium">
                      {totalCount.toLocaleString()}{' '}
                      <span className="text-muted-foreground">
                        {totalCount === 1 ? 'Scraper' : 'Scrapers'}
                      </span>
                    </span>
                  </div>
                )}
                {/* Pagination controls */}
                {totalPages > 1 && (
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                    <Button
                      variant="outline"
                      onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="flex items-center gap-2 w-full sm:w-auto"
                    >
                      <ChevronLeft className="w-4 h-4" />
                      Previous
                    </Button>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">
                        Page {currentPage} of {totalPages}
                      </span>
                    </div>
                    <Button
                      variant="outline"
                      onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className="flex items-center gap-2 w-full sm:w-auto"
                    >
                      Next
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Guarantee Text at the end of the page */}
      <div className="mt-10 mb-6 text-center">
        <p className="text-sm text-success flex items-center justify-center gap-1.5">
          <ShieldCheck className="hidden sm:inline w-5 h-5 md:w-4 md:h-4" />
          <span>
            All scraping services come with a satisfaction guarantee. We review all deliveries for
            quality.
          </span>
        </p>
      </div>
    </>
  );
}
